import React, { useState } from 'react';
import { Project } from '../types';
import { Eye, Youtube, Loader2 } from 'lucide-react';

interface ProjectCardProps {
  project: Project;
  onClick: (project: Project) => void;
}

const formatViews = (views?: number) => {
  if (views === undefined || views === null) return '0';
  if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M';
  if (views >= 1000) return (views / 1000).toFixed(1) + 'K';
  return views.toString();
};

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  const [imageStatus, setImageStatus] = useState({ desktop: false, mobile: false });

  // Fallback Logic:
  // 1. Desktop: try desktopPreviewUrl -> fallback to thumbnailUrl
  // 2. Mobile: try mobilePreviewUrl -> fallback to thumbnailUrl
  const desktopPreviewUrl = project.desktopPreviewUrl || project.thumbnailUrl;
  const mobilePreviewUrl = project.mobilePreviewUrl || project.thumbnailUrl;

  const hasYoutubeLink = !!project.youtubeVideoId || (project.thumbnailUrl && project.thumbnailUrl.includes('ytimg.com'));

  // Day Badge logic
  const dayNumber = project.id; 

  return (
    <div 
      className="group relative glass-panel rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 flex flex-col min-w-0
      border-slate-200 dark:border-white/5 bg-white/40 dark:bg-slate-900/50 hover:border-primary/40 dark:hover:border-primary/40"
      onClick={() => onClick(project)}
    >
      {/* Visual Header / Preview Area */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100 dark:bg-slate-800/30 shrink-0">
        
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-t from-slate-200 via-transparent to-transparent dark:from-slate-950 z-0 opacity-80" />

        {/* Desktop Browser Mockup (Back) */}
        <div className="absolute top-2 left-2 right-6 sm:right-16 bottom-0 bg-white dark:bg-slate-900 rounded-tl-lg rounded-tr-lg border border-slate-300 dark:border-white/10 shadow-2xl overflow-hidden z-10 flex flex-col">
            {/* Browser Toolbar */}
            <div className="h-3 sm:h-5 bg-slate-100 dark:bg-slate-800 flex items-center gap-1 px-2 border-b border-slate-200 dark:border-white/5 shrink-0">
               <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-red-400"></div>
               <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-yellow-400"></div>
               <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-green-400"></div>
            </div>

            {/* Screen Content */}
            <div className="relative flex-1 bg-white dark:bg-slate-950 w-full overflow-hidden group-hover:grayscale-0 transition-all duration-500 grayscale-[30%]">
                {/* Loading State Desktop */}
                {!imageStatus.desktop && (
                   <div className="absolute inset-0 bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center z-20">
                      <Loader2 size={24} className="text-primary animate-spin mb-2" />
                   </div>
                )}
                
                <img 
                    src={desktopPreviewUrl} 
                    alt="Desktop" 
                    className={`w-full h-full object-cover object-top transition-all duration-700 ${imageStatus.desktop ? 'opacity-90 group-hover:opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                    onLoad={() => setImageStatus(prev => ({ ...prev, desktop: true }))}
                    onError={(e) => {
                        // Fallback if image fails
                        (e.target as HTMLImageElement).src = 'https://picsum.photos/600/400?grayscale';
                    }}
                />
            </div>
        </div>

        {/* Mobile Phone Mockup (Front) - Always Visible */}
        <div className="absolute right-1.5 bottom-[-15%] w-[35%] sm:w-[30%] aspect-[9/18] bg-white dark:bg-slate-900 rounded-[0.8rem] sm:rounded-[2rem] border-[3px] sm:border-[6px] border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden z-20 transform rotate-[-6deg] group-hover:rotate-0 group-hover:translate-y-[-10px] transition-all duration-500 origin-bottom-right ring-1 ring-black/5 dark:ring-white/10 group-hover:ring-primary/30">
            {/* Loading State Mobile */}
            {!imageStatus.mobile && (
                <div className="absolute inset-0 bg-slate-50 dark:bg-slate-900 flex items-center justify-center z-20">
                   <Loader2 size={16} className="text-accent animate-spin" />
                </div>
            )}
            
            <img 
                src={mobilePreviewUrl} 
                alt="Mobile" 
                className={`w-full h-full object-cover object-top bg-white transition-opacity duration-500 ${imageStatus.mobile ? 'opacity-100' : 'opacity-0'}`}
                loading="lazy"
                onLoad={() => setImageStatus(prev => ({ ...prev, mobile: true }))}
                onError={(e) => {
                   (e.target as HTMLImageElement).src = 'https://picsum.photos/300/600?grayscale';
                }}
            />
        </div>

        {/* Day Badge */}
        <div className="absolute top-2 left-2 z-30">
          <span className="px-1.5 py-0.5 text-[8px] sm:text-[10px] font-bold tracking-wide uppercase bg-white/90 dark:bg-black/80 backdrop-blur-md rounded border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white shadow-sm">
             Day {dayNumber}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-2 sm:p-3 flex flex-col flex-1 relative z-10">
        <h3 className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-white leading-tight mb-1 truncate pr-1">
            {project.title}
        </h3>
        
        <p className="text-[9px] sm:text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2 mb-2 leading-relaxed h-[2.2em]">
          {project.description}
        </p>

        {/* Bottom Actions */}
        <div className="flex justify-between items-center mt-auto pt-2 border-t border-slate-200/50 dark:border-white/5">
          <div className="flex gap-1 overflow-hidden max-w-[65%]">
             <span className="text-[8px] sm:text-[9px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800/50 rounded text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/5 whitespace-nowrap truncate">
                {project.category}
             </span>
          </div>
          
          <div className="flex items-center gap-2 pl-1">
             {project.views !== undefined && (
                <div className="flex items-center gap-0.5 text-slate-500 dark:text-slate-400 text-[9px]">
                   {hasYoutubeLink ? <Youtube size={10} className="text-red-500" /> : <Eye size={10} />}
                   <span className="font-mono">{formatViews(project.views)}</span>
                </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};